/* eslint-disable @typescript-eslint/no-empty-function */
import React, { JSXElementConstructor, ReactElement } from 'react';
import Modal from '../components/Modal';

type ReactContent = ReactElement<any, string | JSXElementConstructor<any>>;

interface GlobalModalContext {
  isGlobalModalOpen: boolean;
  openGlobalModal: () => void;
  closeGlobalModal: () => void;
  onCloseModal?: () => void;
  handleModalContent: (
    content: ReactContent,
    title: string,
    onCloseModal?: () => void,
  ) => void;
}

const GlobalModalContext = React.createContext({
  isGlobalModalOpen: false,
  openGlobalModal: () => {},
  closeGlobalModal: () => {},
  handleModalContent: (content: ReactContent, title: string) => {},
});
GlobalModalContext.displayName = 'GlobalModalContext';

const useGlobalModalContext = (): GlobalModalContext => {
  const context = React.useContext(GlobalModalContext);
  if (context === undefined) {
    throw new Error(
      'useGlobalModalContext must be used within a GlobalModalProvider',
    );
  }
  return context;
};

export const GlobalModalProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}): JSX.Element => {
  const handleCloseModal = React.useRef(() => {});
  const [globalModalTitle, setGlobalModalTitle] = React.useState<string | null>(
    null,
  );
  const [isGlobalModalOpen, setIsGlobalModalOpen] = React.useState<boolean>(
    false,
  );
  const [
    globalModalContent,
    setGlobalModalContent,
  ] = React.useState<ReactContent | null>(null);

  const openGlobalModal = () => setIsGlobalModalOpen(true);
  const closeGlobalModal = () => {
    setGlobalModalContent(null);
    setGlobalModalTitle(null);
    setIsGlobalModalOpen(false);
    handleCloseModal.current = () => {};
  };

  const handleModalContent = (
    content: ReactContent,
    title: string,
    onCloseModal?: () => void,
  ) => {
    if (content && !globalModalContent) {
      setGlobalModalContent(content);
    }
    if (title && !globalModalTitle) {
      setGlobalModalTitle(title);
    }

    if (onCloseModal) {
      handleCloseModal.current = onCloseModal;
    }
  };

  const context = {
    isGlobalModalOpen,
    openGlobalModal,
    closeGlobalModal,
    handleModalContent,
  };

  return (
    <GlobalModalContext.Provider value={context}>
      {children}
      <Modal
        onClose={closeGlobalModal}
        onAfterClose={handleCloseModal.current}
        title={globalModalTitle || ''}
        open={isGlobalModalOpen}
        isCompleteScreen
      >
        {globalModalContent || <div />}
      </Modal>
    </GlobalModalContext.Provider>
  );
};

export default useGlobalModalContext;
